import imageUrlBuilder from '@sanity/image-url';
import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Head from 'next/head';
import NextImage, { ImageProps as NextImageProps } from 'next/image';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { roundToNearest } from '../../helpers/utils/number';
import { useDebounce } from '../../hooks/useDebounce';
import { ImageType, RatioType } from '../../types';
import { ImageShapeType } from './ImageOptions';

type ImageProps = {
  crop?: ImageType['crop'];
  hotspot?: ImageType['hotspot'];
  preventResize?: boolean;
  ratio?: RatioType;
  shape?: ImageShapeType;
  roundSize?: number;
} & NextImageProps;

export const imageBuilder = imageUrlBuilder({
  projectId: process.env.NEXT_PUBLIC_SANITY_PROJECT_ID,
  dataset: process.env.NEXT_PUBLIC_SANITY_DATASET,
});

const ratioClasses: Record<RatioType, string> = {
  '1/1': 'aspect-1/1',
  '16/9': 'aspect-16/9',
  '2/1': 'aspect-2/1',
  '13/8': 'aspect-13/8',
  '4/3': 'aspect-4/3',
};

const shapeClasses: Record<ImageShapeType, string> = {
  square: '',
  circle: 'rounded-full overflow-hidden',
  rounded: 'rounded-xl overflow-hidden',
};

export const Image = ({
  src,
  width,
  height,
  crop,
  hotspot,
  alt = '',
  layout = 'intrinsic',
  objectFit,
  className,
  preventResize = false,
  priority,
  ratio,
  shape,
  roundSize = 0,
}: ImageProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);

  const [responsiveSrc, setResponsiveSrc] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'loaded'>('loading');

  const [wrapperWidth, setWrapperWidth] = useState<number>(0);
  const [wrapperHeight, setWrapperHeight] = useState<number>(0);
  const debouncedWrapperWidth = useDebounce(wrapperWidth, 500);
  const debouncedWrapperHeight = useDebounce(wrapperHeight, 500);

  let placeHolderSrc;

  // if (ratio) {
  //   height = (+width / +ratio.split(':')[0]) * +ratio.split(':')[1];
  // }

  if (src && typeof src === 'string' && src.indexOf('sanity.io') > -1) {
    placeHolderSrc = getResponsiveImageUrl({
      src,
      width: 16,
      height: 16 / (+width / +height),
      hotspot,
      crop,
      blur: 10,
    });
  }

  const onResize = useCallback(() => {
    if (!src) return;
    if (!wrapperRef?.current) return;
    if (!placeHolderSrc) return;
    if (typeof src !== 'string') return;

    const rect = wrapperRef.current.getBoundingClientRect();

    const w = roundToNearest(roundSize, rect.width);
    const h = roundToNearest(roundSize, rect.height);

    setWrapperWidth(w);
    setWrapperHeight(h);
  }, [src, placeHolderSrc, roundSize]);

  /**
   * Listen to window resizes
   */

  useEffect(() => {
    if (!src) return;
    if (!wrapperRef?.current) return;
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(wrapperRef.current);
    () => resizeObserver.disconnect();
  }, [wrapperRef, src, onResize]);

  /**
   * when the debounced window width changes
   * generate new source for sanity images
   */

  useEffect(() => {
    if (!src) return;
    if (!placeHolderSrc) return;
    if (typeof src !== 'string') return;

    const rect = wrapperRef.current.getBoundingClientRect();

    const newWidth = rect.width;
    let newHeight = rect.height;
    let quality = 85;

    if (preventResize === true) {
      // Do we need to factor in crops as well? https://github.com/sanity-io/hydrogen-sanity-demo/blob/main/src/components/SanityImage.client.jsx#L100
      newHeight = rect.width / (+width / +height);
      quality = null;
    }

    // for layouts where height depends on the image, use image ratio
    if (newHeight === 0) newHeight = newWidth / (+width / +height);

    setResponsiveSrc(
      getResponsiveImageUrl({
        src,
        width: newWidth,
        height: newHeight,
        hotspot,
        crop,
        quality,
      }),
    );
  }, [
    wrapperWidth,
    debouncedWrapperHeight,
    width,
    height,
    src,
    crop,
    hotspot,
    preventResize,
    placeHolderSrc,
  ]);

  const onImageLoad = useCallback(() => {
    setState('loaded');
  }, []);

  if (!src) return null;

  return (
    // disable margin underneath next image until classes can be applied to next image directly
    // https://github.com/vercel/next.js/discussions/22861
    <div
      className={cx('text-0 h-full w-full', {
        ['relative']: !objectFit,
        [ratioClasses[ratio]]: ratio,
        [shapeClasses[shape]]: shape,
      })}
      ref={wrapperRef}
    >
      <AnimatePresence>
        {state === 'loading' && placeHolderSrc && (
          <motion.div
            ref={placeholderRef}
            initial={{ opacity: 0.5 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.01 }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              boxSizing: 'border-box',
              padding: 0,
              border: 'none',
              margin: 'auto',
              display: 'block',
              width: 0,
              height: 0,
              minWidth: '100%',
              maxWidth: '100%',
              minHeight: '100%',
              maxHeight: '100%',
              zIndex: 1,
            }}
          >
            <NextImage
              src={placeHolderSrc}
              className={className}
              alt={alt ?? 'Image'}
              layout={layout}
              width={layout === 'fill' ? null : width}
              height={layout === 'fill' ? null : height}
              priority={priority}
              objectFit={objectFit}
              loader={({ src, width, quality = 85 }) => {
                return `${src}&maxWidth=${width}&q=${quality}`;
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {priority && responsiveSrc && (
        <Head>
          <link rel="preload" as="image" href={responsiveSrc} />
        </Head>
      )}

      <NextImage
        src={responsiveSrc || placeHolderSrc || src}
        className={className}
        alt={alt}
        layout={layout}
        width={layout === 'fill' ? null : width}
        height={layout === 'fill' ? null : height}
        objectFit={objectFit}
        priority={priority}
        loader={({ src, width, quality = 100 }) => {
          return `${src}&maxWidth=${width}&q=${quality}`;
        }}
        quality={85}
        onLoadingComplete={onImageLoad}
      />
    </div>
  );
};

export const ImageMemo = React.memo(Image);

type GetResponsiveImageUrlProps = {
  src: string;
  width: number;
  height: number;
  crop: ImageProps['crop'];
  hotspot: ImageProps['hotspot'];
  blur?: number;
  quality?: number;
};

export function getResponsiveImageUrl({
  src,
  width,
  height,
  crop,
  hotspot,
  blur = 0,
  quality = 85,
}: GetResponsiveImageUrlProps) {
  if (!src) return null;
  const dpr = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
  let newSrc = imageBuilder.image(src).auto('format');
  if (quality) newSrc = newSrc.quality(85);
  if (blur) newSrc = newSrc.blur(blur);
  if (width) newSrc = newSrc.width(Math.ceil(width * dpr));
  if (height) newSrc = newSrc.height(Math.ceil(height * dpr));
  if (hotspot?.x && hotspot?.y)
    newSrc = newSrc.crop('focalpoint').focalPoint(hotspot.x, hotspot.y).fit('crop');
  if (crop?.left && crop?.top && crop?.right && crop?.top)
    newSrc = newSrc.rect(crop.left, crop.top, crop.right, crop.top);

  return newSrc.url();
}
